import Cookies from "js-cookie";

import { auth } from "js/firebase/auth";
import { openWindowAndWaitForItToBeClosed, PopupBlockedError } from "js/core/utilities/selfClosingWindow";

export class MicrosoftAuthError extends Error { }
export const MicrosoftAuthPopupBlockedError = PopupBlockedError;
export class MicrosoftAuthFlowWasInterruptedError extends MicrosoftAuthError { }

interface MicrosoftAuthTokens {
    accessToken: string,
    idToken: string,
    customToken?: string,
}

export const AUTH_SCOPES = ["profile", "email", "openid"];
export const FILE_PICKER_SCOPES = ["profile", "email", "openid", "offline_access", "User.Read", "Files.Read.All"];

async function _openPopup(scope: string): Promise<MicrosoftAuthTokens> {
    // Use unified /microsoft route for all flows
    let url = `${window.location.origin}/microsoft/auth` +
        `?scope=${encodeURIComponent(scope)}` +
        `&continueUrl=${encodeURIComponent("/close")}`;

    // Add user id token if signed in
    if (auth().currentUser) {
        const userIdToken = await auth().currentUser.getIdToken();
        Cookies.set("user_id_token", userIdToken, { sameSite: "lax", secure: window.location.protocol === "https:", expires: new Date(Date.now() + 60 * 1000) });
    }

    const lastSearchParams = await openWindowAndWaitForItToBeClosed(url);

    // Check for errors using unified error handling
    if (lastSearchParams.has("microsoft_auth_error")) {
        const errorMessage = lastSearchParams.get("microsoft_auth_error");
        throw new MicrosoftAuthError(errorMessage);
    }

    // Check for success using unified success parameter
    if (lastSearchParams.get("microsoft_auth_success") !== "true") {
        throw new MicrosoftAuthFlowWasInterruptedError();
    }

    const accessToken = localStorage.getItem("microsoft_auth_access_token");
    const idToken = localStorage.getItem("microsoft_auth_id_token");
    const customToken = localStorage.getItem("microsoft_auth_custom_token");

    if (accessToken) {
        localStorage.removeItem("microsoft_auth_access_token");
    }
    if (idToken) {
        localStorage.removeItem("microsoft_auth_id_token");
    }
    if (customToken) {
        localStorage.removeItem("microsoft_auth_custom_token");
    }

    return { accessToken, idToken, customToken };
}

/**
 * Does Microsoft sign in flow using redirect
 */
export async function authenticate(scope: string, continueUrl: string): Promise<void> {
    // Add user id token if signed in
    if (auth().currentUser) {
        const userIdToken = await auth().currentUser.getIdToken();
        Cookies.set("user_id_token", userIdToken, { sameSite: "lax", secure: window.location.protocol === "https:", expires: new Date(Date.now() + 60 * 1000) });
    }

    // Use unified /microsoft route for all flows
    window.location.href = `/microsoft/auth?scope=${encodeURIComponent(scope)}&continueUrl=${encodeURIComponent(continueUrl)}`;
}

/**
 * Does Microsoft sign in flow using redirect in a separate window (faux popup), returns id token
 */
export async function authenticateWithPopup(scope: string): Promise<MicrosoftAuthTokens> {
    return _openPopup(scope);
}
